import { UseFormReturn, useWatch } from "react-hook-form";
import { ChevronSmallDownIcon, ChevronSmallUpIcon, ChevronUpDownIcon } from "../../icons/chevronUpDown.icon";
import { SortKey, Tag, TagList, TagListFilter } from "../../types/tag";
import { useTranslation } from "../../core/translation.core";
import TagRow from "./tagRow.comp";
import ErrorCenterIcon from "../../icons/exclamationTriangleCenter.icon";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";
import { useQuery } from "@tanstack/react-query";
import tagCore from "../../core/tag.core";

export default function TagListComp({ methods }: { methods: UseFormReturn<TagListFilter, any> }) {
  const { t, msg } = useTranslation();
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["tags"],
    queryFn: tagCore.listTags,
  });

  if (isError) return <ErrorCenterIcon reason={error} />;
  if (isPending) return <LoadingOvalCenterIcon />;

  return (
    <div id="product-section" className="pt-6 lg:pt-0">
      <div className="hidden lg:grid lg:grid-cols-[3fr_3fr_2fr_1fr] gap-12 px-6 py-3 text-sm font-medium">
        <Title title={t(msg.tag.name)} name="name" methods={methods} />
        <Title title={t(msg.tag.slug)} name="slug" methods={methods} />
        <Title title={t(msg.tag.enable)} name="enabled" methods={methods} />
      </div>

      {data!.map((p) => (
        <TagRow tag={p} key={p.id} />
      ))}
    </div>
  );
}

function Title({ methods, title, name }: { methods: UseFormReturn<TagListFilter, any>; title: string; name: SortKey }) {
  const [sort, isSortAsec] = useWatch({
    name: ["sort", "sortAsec"],
    control: methods.control,
  });

  function click() {
    methods.setValue("sort", name);
    methods.setValue("sortAsec", sort !== name ? 1 : isSortAsec === 1 ? -1 : 1);
  }

  return (
    <button id={`sort_${name}`} className="flex items-center gap-2" onClick={click}>
      {title}
      {sort !== name && <ChevronUpDownIcon />}
      {sort === name && isSortAsec === 1 && <ChevronSmallUpIcon />}
      {sort === name && isSortAsec === -1 && <ChevronSmallDownIcon />}
    </button>
  );
}

export const dummyTags: Tag[] = [
  {
    id: "1",
    name: { en: "FreshFlavors", zhHant: "鮮美風味", zhHans: "鲜美风味" },
    slug: { en: "fresh-flavors", zhHant: "xian-mei-feng-wei", zhHans: "xian-mei-feng-wei" },
    enabled: true,
  },
  {
    id: "2",
    name: { en: "TastyBites", zhHant: "美味小吃", zhHans: "美味小吃" },
    slug: { en: "tasty-bites", zhHant: "mei-wei-xiao-chi", zhHans: "mei-wei-xiao-chi" },
    enabled: true,
  },
  {
    id: "3",
    name: { en: "SavoryDelights", zhHant: "鹹香美食", zhHans: "咸香美食" },
    slug: { en: "savory-delights", zhHant: "xian-xiang-mei-shi", zhHans: "xian-xiang-mei-shi" },
    enabled: true,
  },
  {
    id: "4",
    name: { en: "QuickEats", zhHant: "快速美食", zhHans: "快速美食" },
    slug: { en: "quick-eats", zhHant: "kuai-su-mei-shi", zhHans: "kuai-su-mei-shi" },
    enabled: false,
  },
  {
    id: "5",
    name: { en: "GourmetVibes", zhHant: "美食氛圍", zhHans: "美食氛围" },
    slug: { en: "gourmet-vibes", zhHant: "mei-shi-fen-wei", zhHans: "mei-shi-fen-wei" },
    enabled: true,
  },
  {
    id: "6",
    name: { en: "FlavorFusion", zhHant: "風味融合", zhHans: "风味融合" },
    slug: { en: "flavor-fusion", zhHant: "feng-wei-rong-he", zhHans: "feng-wei-rong-he" },
    enabled: true,
  },
  {
    id: "7",
    name: { en: "ComfortCravings", zhHant: "安慰美食", zhHans: "安慰美食" },
    slug: { en: "comfort-cravings", zhHant: "an-wei-mei-shi", zhHans: "an-wei-mei-shi" },
    enabled: false,
  },
  {
    id: "8",
    name: { en: "HeartyMeals", zhHant: "豐盛餐點", zhHans: "丰盛餐点" },
    slug: { en: "hearty-meals", zhHant: "feng-sheng-can-dian", zhHans: "feng-sheng-can-dian" },
    enabled: true,
  },
  {
    id: "9",
    name: { en: "CrispyCravings", zhHant: "香脆誘惑", zhHans: "香脆诱惑" },
    slug: { en: "crispy-cravings", zhHant: "xiang-cui-you-huo", zhHans: "xiang-cui-you-huo" },
    enabled: true,
  },
  {
    id: "10",
    name: { en: "SweetToothFix", zhHant: "滿足甜食慾", zhHans: "满足甜食欲" },
    slug: { en: "sweet-tooth-fix", zhHant: "man-zu-tian-shi-yu", zhHans: "man-zu-tian-shi-yu" },
    enabled: false,
  },
  {
    id: "11",
    name: { en: "FarmToTable", zhHant: "從農場到餐桌", zhHans: "从农场到餐桌" },
    slug: { en: "farm-to-table", zhHant: "cong-nong-chang-dao-can-zhuo", zhHans: "cong-nong-chang-dao-can-zhuo" },
    enabled: true,
  },
  {
    id: "12",
    name: { en: "ZestyTreats", zhHant: "辛香美味", zhHans: "辛香美味" },
    slug: { en: "zesty-treats", zhHant: "xin-xiang-mei-wei", zhHans: "xin-xiang-mei-wei" },
    enabled: true,
  },
  {
    id: "13",
    name: { en: "WholesomeEats", zhHant: "健康美食", zhHans: "健康美食" },
    slug: { en: "wholesome-eats", zhHant: "jian-kang-mei-shi", zhHans: "jian-kang-mei-shi" },
    enabled: true,
  },
  {
    id: "14",
    name: { en: "SpoonfulOfGoodness", zhHant: "一勺美味", zhHans: "一勺美味" },
    slug: { en: "spoonful-of-goodness", zhHant: "yi-shao-mei-wei", zhHans: "yi-shao-mei-wei" },
    enabled: false,
  },
  {
    id: "15",
    name: { en: "BiteSizedJoy", zhHant: "一口幸福", zhHans: "一口幸福" },
    slug: { en: "bite-sized-joy", zhHant: "yi-kou-xing-fu", zhHans: "yi-kou-xing-fu" },
    enabled: true,
  },
  {
    id: "16",
    name: { en: "HomeCookedMagic", zhHant: "家常魔法", zhHans: "家常魔法" },
    slug: { en: "home-cooked-magic", zhHant: "jia-chang-mo-fa", zhHans: "jia-chang-mo-fa" },
    enabled: true,
  },
  {
    id: "17",
    name: { en: "SpiceItUp", zhHant: "加點香料", zhHans: "加点香料" },
    slug: { en: "spice-it-up", zhHant: "jia-dian-xiang-liao", zhHans: "jia-dian-xiang-liao" },
    enabled: false,
  },
  {
    id: "18",
    name: { en: "FoodieFinds", zhHant: "美食發現", zhHans: "美食发现" },
    slug: { en: "foodie-finds", zhHant: "mei-shi-fa-xian", zhHans: "mei-shi-fa-xian" },
    enabled: true,
  },
  {
    id: "19",
    name: { en: "MouthwateringMoments", zhHant: "垂涎時刻", zhHans: "垂涎时刻" },
    slug: { en: "mouthwatering-moments", zhHant: "chui-xian-shi-ke", zhHans: "chui-xian-shi-ke" },
    enabled: true,
  },
  {
    id: "20",
    name: { en: "DelishEveryday", zhHant: "天天美味", zhHans: "天天美味" },
    slug: { en: "delish-everyday", zhHant: "tian-tian-mei-wei", zhHans: "tian-tian-mei-wei" },
    enabled: false,
  },
  // Add more tags similarly until you reach 30
];
